import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// /en/work-permit — EN hub for Thailand Work Permit + Non-B + BOI Visa per nationality
import { createFileRoute, Link } from "@tanstack/react-router";
import { WP_NATIONALITIES, WP_REGION_LABELS_EN, type WpRegion } from "@/data/work-permit-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Briefcase, ShieldCheck, Phone, MessageCircle, ArrowRight, Sparkles, MapPin, Clock, Wallet } from "lucide-react";
import { AIAnswerBox } from "@/components/AIAnswerBox";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_EN } from "@/lib/trust-schema";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/work-permit/")({
  head: () => {
    const url = `${SITE}/en/work-permit`;
    const title = "Thailand Work Permit + Non-B Visa by Nationality · BOI Fast-Track 7 Days | NYC";
    const desc = "Thailand Work Permit + Non-Immigrant B + BOI/EEC Smart Visa for foreign professionals. Send your documents for a free eligibility check.";
    return {
      meta: [
        { title: ctaTitle(title, "/en/work-permit", "en") }, { name: "description", content: ctaDesc(desc, "/en/work-permit", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/work-permit", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/work-permit", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
        { name: "keywords", content: "Thailand work permit, Non-B visa Thailand, BOI Smart Visa, EEC fast-track, foreign worker Thailand, Bangkok work permit, BOI work permit" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks("/work-permit", "/en/work-permit"),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#page`,
              name: title, description: desc, url, inLanguage: "en-US",
              mainEntity: {
                "@type": "ItemList",
                numberOfItems: WP_NATIONALITIES.length,
                itemListElement: WP_NATIONALITIES.map((n, i) => ({
                  "@type": "ListItem", position: i + 1, name: `Work Permit for ${n.demonymEn} nationals`,
                  url: `${SITE}/en/work-permit/${n.slug}`,
                })),
              },
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: "Thailand Work Permit & Non-B Visa Processing",
              serviceType: "Thailand Work Permit & Non-B Visa Processing",
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: "TH",
              offers: { "@type": "Offer", price: "12000", priceCurrency: "THB", description: "Work Permit + Non-B Visa package" },
            }),
          },
          howToSchema({
            name: "How to obtain a Thailand Work Permit + Non-B Visa",
            description: "5-step procedure for the Non-Immigrant B visa and Work Permit",
            totalTime: "P14D",
            estimatedCost: { currency: "THB", value: "12000" },
            inLanguage: "en",
            steps: [
              { name: "Apply for Non-B at Thai Embassy", text: "Submit invitation letter + company docs at the Thai Embassy in your home country (5-7 business days)", url: `${SITE}/en/visa` },
              { name: "Enter Thailand on Non-B", text: "Enter within 90 days — 90-day stay before applying for the 1-year extension" },
              { name: "File Work Permit at MOL", text: "BOI/EEC fast-track 7 days · Standard MOL 10-14 days · government fee THB 3,000/year", url: `${SITE}/en/work-permit` },
              { name: "Extend Non-B to 1 year", text: "Apply for the 1-year extension at Immigration within 30 days before expiry" },
              { name: "Re-Entry Permit", text: "Get Multiple Re-Entry (THB 3,800) to travel out without losing your visa" },
            ],
          }),
          faqPageSchema([
            { q: "What is the minimum salary required for a Thailand Work Permit?", a: "The Ministry of Labour Notification dated 11 Jan 2017 sets minimum monthly salary by nationality: Europe, USA, Canada, Japan, Australia THB 50,000 · South Korea, Singapore, Hong Kong, Taiwan THB 45,000 · India, Indonesia, Middle East, China THB 35,000 · ASEAN (Vietnam, Philippines, Cambodia, Laos, Myanmar) THB 25,000. Salary is verified against PND.1 withholding-tax filings at WP renewal." },
            { q: "How does BOI/EEC fast-track differ from the standard process?", a: "BOI-promoted companies and firms in EEC zones (Amata, WHA, Hemaraj) use One-Stop Service at Chamchuri Square — Work Permit issued in 7 days vs 10-14, plus eligibility for Smart Visa S/T/I/E/O." },
            { q: "Can my family come with me?", a: "Yes. Spouse and biological/adopted children under 20 obtain Non-Immigrant 'O' Dependent visas (Immigration Act B.E. 2522) alongside the main Work Permit holder. Required: legalised marriage certificate, birth certificates, and Apostille or MFA attestation from the country of origin. Each dependent costs THB 1,900 visa fee + THB 1,900 extension; NYC bundles 4 dependents from THB 18,000." },
            { q: "Where do I get documents translated?", a: "Documents must be translated by Ministry of Justice-registered translators (Royal Decree on Court Interpreters B.E. 2540) and certified by the MFA Legalisation Division (Chaeng Wattana). NYC handles both in-house through 6 court-licensed notarial attorneys within 1–2 business days from THB 800/page; MFA legalisation adds 2 business days at THB 800–1,000/document." },
            { q: "How do I change employer?", a: "Cancel the existing WP first, then file a new one within 15 days — otherwise Non-B is auto-cancelled. NYC processes Cancellation + New WP in parallel." },
          ]),
        ]),
        entitySameAsSchema(),
        softwareApplicationSchema({ url: `${SITE}/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing Thailand work-permit + Non-B + BOI tooling for all nationalities." }),
        claimReviewSchema(COMMON_CLAIM_REBUTTALS_EN(`${SITE}/en/work-permit`)),
      ],
    };
  },
  component: WorkPermitHubEn,
});

function WorkPermitHubEn() {
  const byRegion = WP_NATIONALITIES.reduce<Record<string, typeof WP_NATIONALITIES>>((acc, n) => {
    (acc[n.region] ||= []).push(n); return acc;
  }, {});
  return (
    <main className="min-h-screen bg-background">
      <AnswerBoxTop cluster="work-permit" locale="en" />
      <div className="mx-auto max-w-6xl px-4 pt-6">
        <AIAnswerBox
          question="How does NYC handle Thailand Work Permit + Non-B + BOI?"
          answer={`NYC processes Thailand Work Permit, Non-Immigrant B and BOI/EEC Smart Visa end-to-end for ${WP_NATIONALITIES.length} source nationalities — including translation, MFA legalisation, Apostille and dependent visas. BOI fast-track 7 days; standard 14–21 days.`}
          stats={[
            { label: "Nationalities", value: `${WP_NATIONALITIES.length}` },
            { label: "BOI", value: "7 days" },
            { label: "Standard", value: "14–21 days" },
            { label: "From", value: "THB 800/page" },
          ]}
          sourceUrl={`${SITE}/en/work-permit`}
          lang="en"
        />
      </div>
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-6xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/en" className="hover:text-primary">Home</Link>
            <span className="mx-2">/</span>
            <span>Work Permit</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Briefcase className="mr-1 inline h-3 w-3" /> Work Permit + Non-B + BOI Visa
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            Thailand Work Permit + Non-B Visa for Foreign Professionals
          </h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            Covering {WP_NATIONALITIES.length} top source nationalities · BOI/EEC fast-track 7 days · Standard MOL 10-14 days · Includes translation, MFA, Apostille and dependent visas
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">How long and how much does a Thailand Work Permit cost?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              7 days (BOI/EEC) to 14 days (standard MOL). Government fee THB 3,000/year + NYC service THB 12,000 (includes filing, translation, and dependent Non-B for spouse and children). Minimum salary ranges THB 25,000-50,000 by nationality., 99.6% first-time acceptance.
            </p>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3 max-w-3xl">
            <div className="rounded-xl border border-border bg-card p-3"><Clock className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">Processing</div><div className="font-bold">7-14 days</div></div>
            <div className="rounded-xl border border-border bg-card p-3"><Wallet className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">From</div><div className="font-bold">THB 12,000</div></div>
            <div className="rounded-xl border border-border bg-card p-3"><MapPin className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">Coverage</div><div className="font-bold">All 77 provinces</div></div>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> Call +66 92-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">Choose your nationality</h2>
          <p className="mt-2 text-sm text-muted-foreground">Grouped by region · click for minimum salary, processing time and country-specific notes</p>
          <div className="mt-8 space-y-10">
            {(Object.keys(byRegion) as WpRegion[]).map((region) => (
              <div key={region}>
                <h3 className="mb-4 text-lg font-bold text-primary"><MapPin className="mr-1 inline h-4 w-4" />{WP_REGION_LABELS_EN[region]}</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {byRegion[region].map((n) => (
                    <Link
                      key={n.slug}
                      to="/en/work-permit/$nationality"
                      params={{ nationality: n.slug }}
                      className="group rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
                    >
                      <div className="flex items-center gap-2 text-2xl">{n.flag} <span className="text-base font-bold">{n.demonymEn}</span></div>
                      <div className="mt-2 text-xs text-muted-foreground">Min salary THB {n.minSalaryTHB.toLocaleString()} · BOI {n.fastTrackDays}d</div>
                      <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">{n.topIndustries.slice(0, 2).join(" · ")}</div>
                      <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
                        View details <ArrowRight className="h-3 w-3" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl"><ShieldCheck className="mr-2 inline h-6 w-6 text-primary" />Related services</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "Non-B Visa by nationality", d: "Long-stay visas, 40+ countries", to: "/en/visa" as const },
              { t: "20+ Industrial Estates", d: "EEC, Amata, WHA, Lat Krabang", to: "/en/industrial-estates" as const },
              { t: "Certified Translation + MFA", d: "ISO 17100 EN/JP/CN/KR", to: "/en/translate" as const },
              { t: "Apostille (Hague)", d: "60+ countries", to: "/en/apostille" as const },
              { t: "Notarial Services Attorney", d: "POA, Board Resolution", to: "/en/notary" as const },
              { t: "APEC Card (ABTC)", d: "21 economies fast-track", to: "/en/apec" as const },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
                <div className="font-bold">{s.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    
      <PlanningToolsLinks locale="en" />
      </main>
  );
}
